import dayjs from 'dayjs';

import { AddTourEventParams } from '@/domain/project-tour-ontour/actions/add-tour-event.action';
import { AddTourTravelParams } from '@/domain/project-tour-ontour/actions/add-tour-travel.action';
import { BulkAddTourEvent } from '@/domain/project-tour-ontour/actions/bulk-add-tour-event.action';
import {
  TourEvent,
  TourEventBasic,
} from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import { TourSequence } from '@/domain/project-tour-ontour/entities/project-tour-ontour-sequence.entity';
import { TourTravel } from '@/domain/project-tour-ontour/entities/project-tour-ontour-travel.entity';
import { ProjectTourOntourGateway } from '@/domain/project-tour-ontour/gateways/project-tour-ontour.gateway';

import { HttpService } from '../http-service';
import {
  getTourEventsDtoSchema,
  importTourEventsFromOrfeoDtoSchema,
} from './project-tour-event.dto.schema';
import {
  ImportTourEventMapper,
  TourEventMapper,
} from './project-tour-event.mapper';
import { getTourSequencesDtoSchema } from './project-tour-sequence.dto.schema';
import { TourSequenceMapper } from './project-tour-sequence.mapper';
import {
  getTourTravelsDtoSchema,
  tourTravelDtoSchema,
} from './project-tour-travel.dto.schema';
import { TourTravelMapper } from './project-tour-travel.mapper';

export class ApiProjectTourOntourHttpGateway implements ProjectTourOntourGateway {
  private http = new HttpService({
    baseURL: import.meta.env.VITE_API_PROJECT,
  });

  // tour-sequence

  async addSequence(
    organisationId: number,
    projectId: number,
    name: string,
  ): Promise<void> {
    const url = '/tours/sequences';
    const data = {
      organisationId: organisationId,
      tourId: projectId,
      name: name,
    };
    const response = await this.http.post(url, data);
    if (response.status === 201) {
      return;
    } else {
      throw 'error on creation';
    }
  }

  async editSequence(tourSequence: TourSequence): Promise<void> {
    const url = `/tours/sequences/${tourSequence.id}`;
    const data = TourSequenceMapper.prepareEditSequenceDto(tourSequence);
    const response = await this.http.patch(url, data);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on edit';
    }
  }

  async getSequences(tourId: number): Promise<TourSequence[]> {
    const url = `/tours/sequences`;
    const options = { params: { tourId: tourId, $sort: { createdAt: 1 } } };
    const response = await this.http.get(url, options);
    const result = getTourSequencesDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data.data.map((x) => TourSequenceMapper.dtoToEntity(x));
  }

  async deleteSequence(sequenceId: number): Promise<void> {
    const url = `/tours/sequences/${sequenceId}`;
    const response = await this.http.delete(url);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on delete';
    }
  }

  // tour-event

  async addEvent(
    sequenceId: number,
    tourEvent: AddTourEventParams['tourEvent'],
  ): Promise<void> {
    const url = `/tours/events`;
    const data = TourEventMapper.prepareAddEventDto(sequenceId, tourEvent);
    const response = await this.http.post(url, data);
    if (response.status === 201) {
      return;
    } else {
      throw 'error on creation';
    }
  }

  async getEvents(sequenceId: number): Promise<TourEvent[]> {
    const url = `/tours/events`;
    const options = { params: { sequenceId: sequenceId, $limit: 1000 } };
    const response = await this.http.get(url, options);
    const result = getTourEventsDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data.data.map((x) => TourEventMapper.dtoToEntity(x));
  }

  async editEvent(tourEvent: TourEvent): Promise<void> {
    const url = `/tours/events/${tourEvent.id}`;
    const data = TourEventMapper.prepareEditEventDto(tourEvent);
    const response = await this.http.patch(url, data);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on edit';
    }
  }

  async deleteEvent(eventId: number): Promise<void> {
    const url = `/tours/events/${eventId}`;
    const response = await this.http.delete(url);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on delete';
    }
  }

  async bulkAddEvents(
    sequenceId: number,
    tourEvents: BulkAddTourEvent['tourEvents'],
  ) {
    const url = `/tours/events`;
    const data = tourEvents.map((x) =>
      TourEventMapper.prepareAddEventDto(sequenceId, x),
    );
    const response = await this.http.post(url, data, {
      additionalHeaders: { 'X-Service-Method': 'batchInsert' },
    });
    if (response.status === 200) {
      return;
    } else {
      throw 'error on creation';
    }
  }

  // tour-travel

  async addTravel(
    organisationId: number,
    sequenceId: number,
    tourTravel: AddTourTravelParams['tourTravel'],
  ): Promise<TourTravel> {
    const url = `/tours/travels`;
    const data = TourTravelMapper.prepareAddTravelDto(
      organisationId,
      sequenceId,
      tourTravel,
    );
    const response = await this.http.post(url, data);
    if (response.status === 201) {
      const result = tourTravelDtoSchema.safeParse(response.data);
      if (result.success === false) {
        console.error(result.error);
        throw result.error;
      }
      return TourTravelMapper.dtoToEntity(result.data);
    } else {
      throw 'error on creation';
    }
  }

  async getTravels(sequenceId: number): Promise<TourTravel[]> {
    const url = `/tours/travels`;
    const options = {
      params: {
        sequenceId: sequenceId,
        $limit: 1000,
        $sort: { createdAt: 1 },
      },
    };
    const response = await this.http.get(url, options);
    const result = getTourTravelsDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data.data.map((x) => TourTravelMapper.dtoToEntity(x));
  }

  async editTravel(tourTravel: TourTravel): Promise<void> {
    const url = `/tours/travels/${tourTravel.id}`;
    const data = TourTravelMapper.prepareEditTravelDto(tourTravel);
    const response = await this.http.patch(url, data);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on edition';
    }
  }

  async deleteTravel(travelId: number): Promise<void> {
    const url = `/tours/travels/${travelId}`;
    const response = await this.http.delete(url);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on deletion';
    }
  }

  // import

  async importTourEventsFromOrfeo(
    apiToken: string,
    spectacleId: string,
    artistId: string,
    startDate: string,
    endDate: string,
  ): Promise<TourEventBasic[]> {
    const url = '/tours/events-import-orfeo';
    const data = {
      apiToken: apiToken,
      spectacleId: spectacleId,
      artistId: artistId,
      startDate:
        startDate === '' ? startDate : dayjs(startDate).format('YYYY-MM-DD'),
      endDate: endDate === '' ? endDate : dayjs(endDate).format('YYYY-MM-DD'),
    };
    const response = await this.http.post(url, data);
    if (response.status !== 201) {
      throw 'error on import';
    }
    const result = importTourEventsFromOrfeoDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    return result.data.map((x) => ImportTourEventMapper.dtoToEntity(x));
  }
}
